import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from '@/api/query';
import { formatDateTimeDB } from '@/utils/format';
import eventBus from '@/utils/mitt';

type Props = Omit<UseMutationOptions<File, Error, Record<string, string | number>, unknown>, 'mutationFn'> & {
  type: keyof Events;
  addPreparingFile?: (name: string) => void;
};

export const useExportFile = ({ type, addPreparingFile, onSuccess, ...options }: Props) => {
  const exportFileMutation = useMutation({
    mutationFn: (params: Record<string, string | number>) => api.export[type](params),
    onSuccess: (data, variables, context) => {
      getFile(data, variables.filename);
      onSuccess?.(data, variables, context);
    },
    ...options,
  });

  useEffect(() => {
    const handleTriggerMutation = (data: Record<string, string | number>) => {
      const filename = `${type}-${formatDateTimeDB(new Date())}.xlsx`;
      addPreparingFile?.(filename);
      exportFileMutation.mutate({
        ...data,
        filename: filename,
      });
    };

    eventBus.on(type, handleTriggerMutation);

    return () => {
      eventBus.off(type, handleTriggerMutation);
    };
  }, [addPreparingFile, exportFileMutation, type]);

  return exportFileMutation;
};

const getFile = (data: File, filename: string | number) => {
  const blob = new Blob([data]);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}`;
  a.click();
  window.URL.revokeObjectURL(url);
};
